import { Button, Grid, Menu, NumberInput, Text } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { IconChevronDown, IconClock } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import WeekdaySelector from './WeekdaySelector';

const scopes = ['week', 'month', 'year'];

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const defaultSchedule = {
  periodicity: 1,
  scope: null,
  days: [],
  months: [],
  hours: 9,
  minutes: 0,
  timeZoneOffset: new Date().getTimezoneOffset(),
  lastRunAt: {},
};

const ScheduleSelector = ({ schedule: passedSchedule, updateSchedule }) => {
  const [schedule, setSchedule] = useState(passedSchedule || { ...defaultSchedule, days: [] });

  useEffect(() => {
    const isSelectedTimeValid = validateSelectedTime(schedule);
    if (isSelectedTimeValid && JSON.stringify(schedule) !== JSON.stringify(passedSchedule)) {
      updateSchedule(schedule);
    }
  }, [schedule, passedSchedule, updateSchedule]);

  const validateSelectedTime = (schedule) => {
    if (!schedule.periodicity) {
      return false;
    }

    if (schedule.scope === '') {
      return false;
    }

    if (!Array.isArray(schedule.days) || schedule.days.length === 0) {
      return false;
    }

    return true;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setSchedule((prevSelectedTime) => {
      const newSelectedTime = { ...prevSelectedTime, timeZoneOffset: new Date().getTimezoneOffset() };

      if (name === 'periodicity') {
        newSelectedTime.periodicity = parseInt(value);
      }

      if (name === 'scope') {
        newSelectedTime.scope = value;
        newSelectedTime.days = [];
      }

      if (name === 'days') {
        if (['month', 'year'].includes(newSelectedTime.scope)) {
          newSelectedTime.days = [value];
        }
        if (newSelectedTime.scope === 'week') {
          newSelectedTime.days = value;
        }
      }

      if (name === 'months') {
        newSelectedTime.months = [value];
      }

      if (name === 'time') {
        newSelectedTime.hours = value.getHours();
        newSelectedTime.minutes = value.getMinutes();
      }

      return newSelectedTime;
    });
  };

  const timeRef = useRef(null);

  const scopeItems = scopes.map((item) => (
    <Menu.Item key={item} onClick={() => handleChange({ target: { name: 'scope', value: item } })}>
      {item}
    </Menu.Item>
  ));

  let monthDayItems = [];
  for (let i = 1; i < 32; i++) {
    monthDayItems.push(
      <Menu.Item key={i} onClick={() => handleChange({ target: { name: 'days', value: i } })}>
        {i}
      </Menu.Item>
    );
  }

  const monthItems = months.map((month, i) => (
    <Menu.Item key={i} onClick={() => handleChange({ target: { name: 'months', value: i } })}>
      {month}
    </Menu.Item>
  ));

  const getTimeValue = () => {
    const hours = schedule.hours >= 0 && schedule.hours <= 23 ? schedule.hours : 9;
    const minutes = schedule.minutes >= 0 && schedule.minutes <= 59 ? schedule.minutes : 0;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleTimeChange = (event) => {
    const value = event.target.value;
    if (value) {
      const [hours, minutes] = value.split(':').map(Number);
      const date = new Date();
      date.setHours(hours);
      date.setMinutes(minutes);
      handleChange({ target: { name: 'time', value: date } });
    }
  };

  const timePicker = (
    <TimeInput ref={timeRef} value={getTimeValue()} onChange={handleTimeChange} leftSection={<IconClock size={16} />} />
  );

  return (
    <Grid py="sm" align="center">
      <Grid.Col span="content">
        <Text size="sm" c="dimmed">
          Every
        </Text>
        <NumberInput
          value={schedule.periodicity}
          min={1}
          max={100}
          onChange={(value) => handleChange({ target: { name: 'periodicity', value } })}
          style={{ width: 80 }}
        />
      </Grid.Col>

      <Grid.Col span="content">
        <Text size="sm" c="dimmed">
          &nbsp;
        </Text>
        <Menu>
          <Menu.Target>
            <Button variant="default" rightSection={<IconChevronDown size={14} />}>
              {schedule.scope ? schedule.scope : 'Select'}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>{scopeItems}</Menu.Dropdown>
        </Menu>
      </Grid.Col>

      {schedule.scope && (
        <Grid.Col span="content" pt="xl">
          <Text c="dimmed">on</Text>
        </Grid.Col>
      )}

      <Grid.Col span="content">
        {schedule.scope && (
          <Text size="sm" c="dimmed">
            Day
          </Text>
        )}

        {(schedule.scope === 'month' || schedule.scope === 'year') && (
          <Menu>
            <Menu.Target>
              <Button variant="default" rightSection={<IconChevronDown size={14} />}>
                {schedule.days?.length > 0 ? schedule.days[0] : 'Select'}
              </Button>
            </Menu.Target>
            <Menu.Dropdown style={{ maxHeight: 200, overflowY: 'auto' }}>{monthDayItems}</Menu.Dropdown>
          </Menu>
        )}

        {schedule.scope === 'week' && (
          <WeekdaySelector
            values={schedule.days}
            setValues={(value) => handleChange({ target: { name: 'days', value } })}
          />
        )}
      </Grid.Col>

      <Grid.Col span="content">
        {schedule.scope === 'year' && (
          <>
            <Text size="sm" c="dimmed">
              Month
            </Text>
            <Menu>
              <Menu.Target>
                <Button variant="default" rightSection={<IconChevronDown size={14} />}>
                  {schedule.months?.length > 0 ? months[schedule.months[0]] : 'Select'}
                </Button>
              </Menu.Target>
              <Menu.Dropdown style={{ maxHeight: 200, overflowY: 'auto' }}>{monthItems}</Menu.Dropdown>
            </Menu>
          </>
        )}
      </Grid.Col>

      {schedule.days?.length > 0 ? (
        <>
          <Grid.Col span="content" pt="xl">
            <Text c="dimmed">at</Text>
          </Grid.Col>
          <Grid.Col span="content">
            <Text size="sm" c="dimmed">
              &nbsp;
            </Text>
            {timePicker}
          </Grid.Col>
        </>
      ) : null}
    </Grid>
  );
};

export default ScheduleSelector;
